'use client'

import Link from 'next/link'
import { Phone, Mail, MapPin, ExternalLink, ChevronUp, ChevronDown } from 'lucide-react'
import Image from 'next/image'
import { useSettings } from '@/contexts/SettingsContext'
import { useState, useEffect } from 'react'
import { track } from '@/lib/ga'

type NavigationItem = {
  name: string;
  href: string;
  isExternal?: boolean;
}

const navigation: {
  services: NavigationItem[];
  company: NavigationItem[];
  support: NavigationItem[];
  resources: NavigationItem[];
  legal: NavigationItem[];
} = {
  services: [
    { name: 'Aircraft Leasing', href: '/aircraft-leasing' },
    { name: 'MRO Services', href: '/mro' },
    { name: 'Aircraft & Parts Listings', href: '/for-sale' },
  ],
  company: [
    { name: 'About Us', href: '/about' },
  ],
  support: [
    { name: 'Q&A', href: '/aircraft-leasing#aircraft-leasing-faq' },
    { name: 'Contact Us', href: '/contact' },
  ],
  resources: [
    { name: 'Admin Login', href: '/admin/login' },
  ],
  legal: [
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Terms of Service', href: '/terms' },
    { name: 'Cookie Policy', href: '/cookies' },
  ],
}

// Social links removed per request

export default function Footer() {
  const { settings, loading } = useSettings()
  const [showScrollTop, setShowScrollTop] = useState(false)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    services: false,
    company: false,
    support: false,
    resources: false,
  })
  const [ctaHeight, setCtaHeight] = useState(0)
  const [isMobile, setIsMobile] = useState(false)

  // Mobile-optimized scroll to top functionality with throttling
  useEffect(() => {
    let ticking = false
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollY = window.scrollY
          setShowScrollTop(scrollY > 400)
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Track mobile sticky CTA visibility and height (mobile only)
  useEffect(() => {
    const checkMobile = () => window.innerWidth <= 640 // sm breakpoint
    setIsMobile(checkMobile())

    const updateCTAHeight = () => {
      if (!checkMobile()) {
        setCtaHeight(0)
        return
      }

      const ctaElement = document.querySelector('[data-cta-location="sticky-mobile"]')
      if (ctaElement) {
        const rect = ctaElement.getBoundingClientRect()
        const windowHeight = window.innerHeight
        // CTA is visible if it's in the viewport
        const isVisible = rect.top < windowHeight && rect.bottom >= 0
        setCtaHeight(isVisible ? rect.height : 0)
      } else {
        setCtaHeight(0)
      }
    }

    // Initial check
    updateCTAHeight()

    // Observe CTA visibility changes
    const observer = new MutationObserver(updateCTAHeight)
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class']
    })

    // Also update on scroll and resize
    const handleScrollOrResize = () => {
      if (checkMobile()) {
        updateCTAHeight()
      }
      setIsMobile(checkMobile())
    }

    window.addEventListener('scroll', handleScrollOrResize, { passive: true })
    window.addEventListener('resize', handleScrollOrResize, { passive: true })

    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', handleScrollOrResize)
      window.removeEventListener('resize', handleScrollOrResize)
    }
  }, [])

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }

  // Use settings if available, otherwise use defaults
  const companyName = settings?.companyName || 'Spitzer Helicopters'
  const companyPhone = settings?.companyPhone || '(310) 614-0986'
  const companyEmail = settings?.companyEmail || 'info@spitzerair.com'
  const companyAddress = settings?.companyAddress || '5158 Clareton Dr, Unit 1147'
  const companyCity = settings?.companyCity || 'Agoura Hills'
  const companyState = settings?.companyState || 'CA'
  const companyZip = settings?.companyZip || '91376'
  const businessHours = settings?.businessHours || '24/7 Support Available'
  // Social links removed per request

  // GA4 tracking for footer links
  const handleFooterLinkClick = (linkName: string, linkUrl: string) => {
    track('footer_link_click', { location: 'footer', link: linkName })
  }


  return (
    <>
      <footer className="relative text-gray-900 overflow-hidden" role="contentinfo">
        {/* Image background */}
        <Image
          src="/images/models/anna-sovetkina-up-aqimqf5w-unsplash.jpg"
          alt="Cloudscape background"
          fill
          priority={false}
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/90 via-white/92 to-white/95"></div>
        
        <div className="relative mx-auto max-w-7xl px-6 py-8 sm:py-12 lg:py-14 lg:px-8">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-8">
            
            {/* Company Info & Logo */}
            <div className="lg:col-span-1 space-y-3 sm:space-y-6">
              <div className="flex justify-center -my-6 sm:my-0" suppressHydrationWarning>
                <Image
                  src="/images/models/Logos/Spitzer Black.webp"
                  alt="Spitzer Helicopter Leasing - Professional Aircraft Leasing Services"
                  width={800}
                  height={280}
                  className="h-36 sm:h-40 w-auto transition-transform duration-300 hover:scale-105"
                  priority={true}
                />
              </div>
              
            </div>

            {/* Navigation Links - Desktop */}
            <div className="hidden md:grid md:grid-cols-3 lg:col-span-3 gap-8">
              {/* Services */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-300">Services</h3>
                <ul className="space-y-3">
                  {navigation.services.map((item) => (
                    <li key={item.name}>
                      <Link 
                        href={item.href} 
                        className="text-sm text-gray-700 hover:text-gray-900 hover:translate-x-1 transition-all duration-200 inline-block focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 focus:ring-offset-white rounded px-1 font-medium"
                        onClick={() => handleFooterLinkClick(item.name, item.href)}
                      >
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Company & Support Combined */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-300">Company & Support</h3>
                <ul className="space-y-3">
                  {[...navigation.company, ...navigation.support].map((item) => (
                    <li key={item.name}>
                      <Link 
                        href={item.href} 
                        className="text-sm text-gray-700 hover:text-gray-900 hover:translate-x-1 transition-all duration-200 inline-flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 focus:ring-offset-white rounded px-1 font-medium"
                        onClick={() => handleFooterLinkClick(item.name, item.href)}
                        target={item.isExternal ? '_blank' : undefined}
                        rel={item.isExternal ? 'noopener noreferrer' : undefined}
                      >
                        {item.name}
                        {item.isExternal && <ExternalLink className="h-3 w-3 opacity-60" />}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Resources & Legal */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-300">Resources</h3>
                <ul className="space-y-3">
                  {[...navigation.resources, ...navigation.legal].map((item) => (
                    <li key={item.name}>
                      <Link 
                        href={item.href} 
                        className="text-sm text-gray-700 hover:text-gray-900 hover:translate-x-1 transition-all duration-200 inline-flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 focus:ring-offset-white rounded px-1 font-medium"
                        onClick={() => handleFooterLinkClick(item.name, item.href)}
                        target={item.isExternal ? '_blank' : undefined}
                        rel={item.isExternal ? 'noopener noreferrer' : undefined}
                      >
                        {item.name}
                        {item.isExternal && <ExternalLink className="h-3 w-3 opacity-60" />}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Mobile Collapsible Navigation */}
            <div className="md:hidden lg:col-span-3 space-y-3">
              {Object.entries({
                services: navigation.services,
                'company & support': [...navigation.company, ...navigation.support],
                resources: [...navigation.resources, ...navigation.legal]
              }).map(([sectionKey, items]) => (
                <div key={sectionKey} className="border border-gray-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleSection(sectionKey)}
                    className="w-full flex items-center justify-between p-3 text-left bg-gray-50 hover:bg-gray-100 transition-colors duration-200"
                    aria-expanded={expandedSections[sectionKey]}
                  >
                    <span className="text-sm font-semibold text-gray-900 capitalize">{sectionKey}</span>
                    {expandedSections[sectionKey] ? (
                      <ChevronUp className="h-4 w-4 text-gray-600" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-600" />
                    )}
                  </button>
                  {expandedSections[sectionKey] && (
                    <div className="p-3 bg-white border-t border-gray-300">
                      <ul className="space-y-2">
                        {items.map((item) => (
                          <li key={item.name}>
                            <Link 
                              href={item.href} 
                              className="text-sm text-gray-700 hover:text-gray-900 transition-colors duration-200 inline-flex items-center gap-1 font-medium"
                              onClick={() => handleFooterLinkClick(item.name, item.href)}
                              target={item.isExternal ? '_blank' : undefined}
                              rel={item.isExternal ? 'noopener noreferrer' : undefined}
                            >
                              {item.name}
                              {item.isExternal && <ExternalLink className="h-3 w-3 opacity-60" />}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>


          {/* Contact Information Cards removed per request */}

          {/* Bottom Section */}
          <div className="mt-6 sm:mt-12 pt-4 sm:pt-6 border-t border-gray-300">
            <div className="text-center">
              <div className="text-sm text-gray-600">© 2025 Sky Group. All rights reserved.</div>
            </div>
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          id="scroll-top-fab"
          onClick={scrollToTop}
          className="fixed right-4 sm:right-8 z-40 bg-gray-900 hover:bg-gray-800 text-white p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-gray-600 focus:ring-offset-2"
          style={{
            bottom: isMobile && ctaHeight > 0
              ? `calc(${ctaHeight}px + 2.5rem + env(safe-area-inset-bottom, 0px))`
              : 'calc(1rem + env(safe-area-inset-bottom, 0px))',
            width: '44px',
            height: '44px',
          }}
          aria-label="Scroll to top"
        >
          <ChevronUp className="h-5 w-5" />
        </button>
      )}
    </>
  )
}
